import { PocList, PocListItem } from 'commons/components/components-wrapper';
import { OwcListItemCustomEvent } from '@one/web-components/dist/types/components';
import { memo, useCallback } from 'react';
import { SelectOptionPlaceholderTextStyled } from './select.style';

export type SelectOption = {
    value: string | number;
    text: string;
    secondaryText?: string;
    prefix?: React.ReactElement;
    disabled?: boolean;
    isPlaceholder?: boolean;
    children?: SelectOption[];
};

type SelectOptionItemProps = {
    option: SelectOption;
    expanded?: boolean;
    name: string;
    optionKey: string;
    onToggleExpanded: (optionKey: string, isExpanded: boolean) => void;
};

const renderOptionText = (option: SelectOption) =>
    option.isPlaceholder ? (
        <SelectOptionPlaceholderTextStyled data-testid="select-option-placeholder-text">
            {option.text}
        </SelectOptionPlaceholderTextStyled>
    ) : (
        option.text
    );

export const SelectOptionItem = memo((props: SelectOptionItemProps) => {
    const { option, expanded, name, optionKey, onToggleExpanded } = props;

    const hasChildren = option.children != null && option.children.length > 0;

    const handleExpandedChange = useCallback(
        (event: OwcListItemCustomEvent<boolean>) => {
            if (hasChildren) {
                onToggleExpanded(optionKey, event.detail === true);
            }
        },
        [hasChildren, onToggleExpanded, optionKey]
    );

    return (
        <PocListItem
            value={option.value}
            disabled={option.disabled}
            data-testid={`select-option-${name}-${optionKey}`}
            expanded={hasChildren ? expanded : undefined}
            onExpandedChange={hasChildren ? handleExpandedChange : undefined}
        >
            {option.children ? (
                <>
                    {renderOptionText(option)}
                    {option.secondaryText ? <span slot="secondary">{option.secondaryText}</span> : undefined}
                    <PocList slot="children">
                        {option.children.map((child) => (
                            <PocListItem key={child.value} value={child.value}>
                                <div slot="prefix">{child.prefix}</div>
                                {renderOptionText(child)}
                                <span slot="secondary">{child.secondaryText}</span>
                            </PocListItem>
                        ))}
                    </PocList>
                </>
            ) : (
                <>
                    <div slot="prefix">{option.prefix}</div>
                    {renderOptionText(option)}
                    {option.secondaryText ? <span slot="secondary">{option.secondaryText}</span> : undefined}
                </>
            )}
        </PocListItem>
    );
});
